// Static preset data for the onboarding wizard.
// Ported from the approved klok_onboarding_wizard prototype. This is the real
// mechanism behind the mockup's "AI suggestions": a static, editable seed map
// that pre-fills each step. Nothing here calls an API — steps persist the
// (possibly edited) values through the existing per-module request actions.

// ---- Industry → org structure seed ---------------------------------------
export const INDUSTRY = {
  "Restaurant & F&B": {
    departments: ["Kitchen", "Floor Staff", "Management", "Delivery", "Finance & HR"],
    grades: ["Senior Mgmt (L4)", "Mid-Level (L3)", "Junior (L2)", "Entry (L1)"],
    designations: ["Head Chef", "Sous Chef", "Captain", "Waiter", "Cashier", "Rider"],
    titles: ["Executive Chef", "Kitchen Staff", "Service Staff", "Manager", "Ops Head"],
  },
  Manufacturing: {
    departments: ["Production", "Quality Control", "Warehouse", "Maintenance", "Admin & HR"],
    grades: ["Plant Head (L5)", "Manager (L4)", "Supervisor (L3)", "Operator (L2)", "Helper (L1)"],
    designations: ["Production Manager", "QC Inspector", "Line Supervisor", "Machine Operator", "Storekeeper", "Technician"],
    titles: ["Plant Manager", "Shift Incharge", "QC Lead", "Operator", "Store Officer"],
  },
  Technology: {
    departments: ["Engineering", "Product", "Design", "QA", "Sales & Marketing", "People Ops"],
    grades: ["Lead (L5)", "Senior (L4)", "Mid (L3)", "Junior (L2)", "Intern (L1)"],
    designations: ["Software Engineer", "Product Manager", "UI/UX Designer", "QA Engineer", "DevOps Engineer", "Account Manager"],
    titles: ["Engineering Lead", "Senior Developer", "Product Owner", "Designer", "QA Analyst"],
  },
  Healthcare: {
    departments: ["Clinical", "Nursing", "Laboratory", "Pharmacy", "Reception", "Admin & HR"],
    grades: ["Consultant (L5)", "Senior (L4)", "Medical Officer (L3)", "Staff (L2)", "Support (L1)"],
    designations: ["Consultant", "Medical Officer", "Staff Nurse", "Lab Technician", "Pharmacist", "Receptionist"],
    titles: ["Chief Medical Officer", "Department Head", "Nurse", "Lab Lead", "Pharmacy Incharge"],
  },
  "Retail & E-Com": {
    departments: ["Store Operations", "Warehouse", "Customer Service", "Marketing", "Finance & HR"],
    grades: ["Regional Head (L5)", "Store Manager (L4)", "Supervisor (L3)", "Associate (L2)", "Trainee (L1)"],
    designations: ["Store Manager", "Sales Associate", "Cashier", "Inventory Officer", "CS Representative", "Packer"],
    titles: ["Area Manager", "Store Lead", "Sales Staff", "Warehouse Staff", "Support Agent"],
  },
  Education: {
    departments: ["Academics", "Administration", "Examinations", "Admissions", "Finance & HR"],
    grades: ["Principal (L5)", "HOD (L4)", "Senior Teacher (L3)", "Teacher (L2)", "Assistant (L1)"],
    designations: ["Principal", "Head of Department", "Senior Teacher", "Teacher", "Lab Assistant", "Admin Officer"],
    titles: ["Principal", "Coordinator", "Faculty", "Teaching Assistant", "Admin Staff"],
  },
  Finance: {
    departments: ["Operations", "Risk & Compliance", "Treasury", "Customer Relations", "IT", "HR"],
    grades: ["VP (L5)", "AVP (L4)", "Manager (L3)", "Officer (L2)", "Associate (L1)"],
    designations: ["Branch Manager", "Relationship Manager", "Compliance Officer", "Teller", "Risk Analyst", "Operations Officer"],
    titles: ["Vice President", "Branch Head", "Relationship Lead", "Officer", "Analyst"],
  },
  Other: {
    departments: ["Operations", "Sales", "Administration", "Finance", "Human Resources"],
    grades: ["Senior Mgmt (L4)", "Manager (L3)", "Executive (L2)", "Entry (L1)"],
    designations: ["Manager", "Team Lead", "Executive", "Officer", "Assistant"],
    titles: ["Department Head", "Lead", "Executive", "Officer", "Support Staff"],
  },
};

export const INDUSTRY_LIST = Object.keys(INDUSTRY);

// ---- Country → currency / timezone hint / holidays -----------------------
// currency/timezone codes are matched by name against the live option lists
// (getCurrencies / getTimezones); they are hints for pre-selection only.
export const COUNTRY = {
  PK: { label: "Pakistan", currency: "PKR", tzHint: "UTC+5", holidayKey: "PK" },
  AE: { label: "UAE", currency: "AED", tzHint: "UTC+4", holidayKey: "AE" },
  SA: { label: "Saudi Arabia", currency: "SAR", tzHint: "UTC+3", holidayKey: "SA" },
  GB: { label: "United Kingdom", currency: "GBP", tzHint: "UTC+0", holidayKey: "GB" },
};

export const HOLIDAYS = {
  PK: { label: "Pakistan 2025", days: [["Kashmir Day", "2025-02-05"], ["Pakistan Day", "2025-03-23"], ["Eid ul-Fitr", "2025-03-31"], ["Labour Day", "2025-05-01"], ["Eid ul-Adha", "2025-06-07"], ["Independence Day", "2025-08-14"], ["Iqbal Day", "2025-11-09"], ["Quaid Day", "2025-12-25"]] },
  AE: { label: "UAE 2025", days: [["New Year", "2025-01-01"], ["Eid ul-Fitr", "2025-03-31"], ["Arafat Day", "2025-06-05"], ["Eid ul-Adha", "2025-06-06"], ["Islamic New Year", "2025-06-26"], ["Prophet's Birthday", "2025-09-04"], ["Commemoration Day", "2025-12-01"], ["National Day", "2025-12-02"]] },
  SA: { label: "Saudi Arabia 2025", days: [["Founding Day", "2025-02-22"], ["Eid ul-Fitr", "2025-03-31"], ["Arafat Day", "2025-06-05"], ["Eid ul-Adha", "2025-06-06"], ["National Day", "2025-09-23"]] },
  GB: { label: "United Kingdom 2025", days: [["New Year", "2025-01-01"], ["Good Friday", "2025-04-18"], ["Easter Monday", "2025-04-21"], ["Early May BH", "2025-05-05"], ["Spring BH", "2025-05-26"], ["Summer BH", "2025-08-25"], ["Christmas", "2025-12-25"], ["Boxing Day", "2025-12-26"]] },
};

// ---- Leave-type minimums (labour-law defaults, Pakistan-oriented) ---------
// acc_count = days/quota granted per accrual cycle. minDays = legal floor.
export const LEAVE_PRESETS = [
  { name: "Annual Leave", acc_count: 14, minDays: 14, is_auto_allotment: true, carry_forward: true },
  { name: "Sick Leave", acc_count: 10, minDays: 10, is_auto_allotment: true, carry_forward: false },
  { name: "Casual Leave", acc_count: 10, minDays: 10, is_auto_allotment: true, carry_forward: false },
  { name: "Maternity Leave", acc_count: 84, minDays: 84, is_auto_allotment: true, carry_forward: false },
  { name: "Hajj Leave", acc_count: 30, minDays: 30, is_auto_allotment: false, carry_forward: false },
];

// ---- Shift / time-slot default (Morning) ---------------------------------
export const SHIFT_PRESET = {
  name: "Morning Shift",
  start: "09:00",
  end: "18:00",
  start_buffer_minutes: 30,
  end_buffer_minutes: 10,
  punch_start_buffer_minutes: 1,
  punch_end_buffer_minutes: 360,
};

// ---- Pay-item seed (name + intent; type/formula resolved from related-data)
export const PAYITEM_PRESETS = [
  { name: "Gross Salary", mandatory: true, hint: "Base monthly salary" },
  { name: "Absent Deduction", mandatory: true, hint: "Per-day cut for absent days" },
  { name: "Income Tax", mandatory: true, hint: "FBR slabs" },
  { name: "EOBI", mandatory: false, hint: "PKR 370/mo employer" },
  { name: "Provident Fund", mandatory: false, hint: "Employee retirement" },
  { name: "House Rent", mandatory: false, hint: "% of gross" },
  { name: "Medical Allowance", mandatory: false, hint: "Fixed allowance" },
];

// Resolve the full preset bundle for the chosen industry + country.
export const resolvePresets = (industry, countryCode) => {
  const ind = INDUSTRY[industry] || INDUSTRY.Other;
  const ctry = COUNTRY[countryCode] || COUNTRY.PK;
  const holidays = HOLIDAYS[ctry.holidayKey] || HOLIDAYS.PK;
  return {
    industry: industry || "Other",
    country: ctry,
    org: {
      departments: [...ind.departments],
      grades: [...ind.grades],
      designations: [...ind.designations],
      titles: [...ind.titles],
    },
    leave: LEAVE_PRESETS,
    shift: SHIFT_PRESET,
    holidays: holidays.days.map(([name, date]) => ({ name, date })),
    payitems: PAYITEM_PRESETS,
  };
};
